// Restores one backup over a tenant's live database. Same pattern as
// DeleteTenantDialog: a typed-slug confirmation, then a phase bar driven by
// the SSE stream from POST /platform-api/tenants/:id/restore
// (scripts/lib/restoreTenant.js).
//
// The confirmation spells out the order of operations, because the safety
// copy is what makes this reversible: the current data is backed up to the
// tenant's storage FIRST, and the restore itself is one transaction, so a
// failure at any step leaves the tenant with the data it had.
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
} from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { streamRestore, type StepEvent, type RestoreCompleteEvent } from '../lib/platformApi/provisionStream';
import type { TenantBackup } from './types';
import { formatBytes } from './format';

const PHASES: { step: string; label: string }[] = [
  { step: 'download', label: 'Checking the backup' },
  { step: 'safety', label: 'Backing up current data' },
  { step: 'restore', label: 'Restoring' },
  { step: 'migrate', label: 'Updating schema' },
  { step: 'finish', label: 'Finishing' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: number;
  slug: string;
  backup: TenantBackup;
  storageLabel: string;
  onRestored: () => void;
}

type Phase = 'confirm' | 'running' | 'done';

export function RestoreBackupDialog({ open, onOpenChange, tenantId, slug, backup, storageLabel, onRestored }: Props) {
  const [phase, setPhase] = useState<Phase>('confirm');
  const [typedSlug, setTypedSlug] = useState('');
  const [currentStep, setCurrentStep] = useState<StepEvent | null>(null);
  const [log, setLog] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [result, setResult] = useState<RestoreCompleteEvent | null>(null);

  const currentIndex = currentStep ? PHASES.findIndex(p => p.step === currentStep.step) : -1;
  const taken = backup.created_at ? new Date(backup.created_at).toLocaleString() : backup.key;

  const run = async () => {
    setPhase('running'); setError('');
    try {
      await streamRestore(tenantId, { backupKey: backup.key, confirmSlug: typedSlug }, {
        onStep: (e) => { setCurrentStep(e); setLog(l => [...l, e.message]); },
        onComplete: (e) => { setResult(e); setPhase('done'); onRestored(); },
        onError: (e) => setError(e.message),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  // Not closable mid-run: the restore continues server-side regardless, and
  // closing would hide whether it finished.
  const close = (next: boolean) => {
    if (!next && phase === 'running' && !error) return;
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        {phase === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>Restore '{slug}' from this backup?</DialogTitle>
              <DialogDescription>
                Every record in this hospital's database will be replaced with the backup taken <strong>{taken}</strong>{' '}
                ({formatBytes(backup.bytes)}). Anything entered after that time is removed from the live database.
              </DialogDescription>
            </DialogHeader>
            <ol style={{ fontSize: 12.5, color: '#374151', margin: '4px 0 14px', paddingLeft: 18, lineHeight: 1.6 }}>
              <li>The <strong>current data is backed up first</strong> to {storageLabel}. If that fails, nothing is restored.</li>
              <li>The backup is loaded in one transaction — if anything fails, the current data stays exactly as it is.</li>
              <li>Migrations newer than the backup are applied.</li>
              <li>The tenant <strong>stays suspended</strong>. Check the data, then reactivate it.</li>
            </ol>
            <div style={{ margin: '4px 0 16px' }}>
              <label className="lbl">Type <code>{slug}</code> to confirm</label>
              <input value={typedSlug} onChange={e => setTypedSlug(e.target.value)} placeholder={slug} autoFocus />
            </div>
            <DialogFooter>
              <DialogClose asChild><Button variant="ghost">Cancel</Button></DialogClose>
              <Button variant="danger" disabled={typedSlug !== slug} onClick={run}>Restore backup</Button>
            </DialogFooter>
          </>
        )}

        {phase === 'running' && (
          <>
            <DialogHeader>
              <DialogTitle>Restoring '{slug}'…</DialogTitle>
              <DialogDescription>Stay on this screen until this finishes.</DialogDescription>
            </DialogHeader>
            <div style={{ display: 'flex', gap: 6, margin: '4px 0 14px' }}>
              {PHASES.map((p, i) => (
                <div key={p.step} style={{
                  flex: 1, height: 8, borderRadius: 4,
                  background: error && i === currentIndex ? '#DC2626' : i < currentIndex || (i === currentIndex && currentStep?.status === 'done') ? '#16A34A' : i === currentIndex ? '#F59E0B' : '#E5E7EB',
                  transition: 'background .2s ease',
                }} />
              ))}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>
              {currentStep ? PHASES.find(p => p.step === currentStep.step)?.label ?? currentStep.message : 'Starting…'}
            </div>
            <div style={{ background: '#0F172A', color: '#94A3B8', fontFamily: 'var(--mono)', fontSize: 11, borderRadius: 8, padding: 12, maxHeight: 180, overflowY: 'auto' }}>
              {log.map((line, i) => <div key={i}>{line}</div>)}
            </div>
            {error && (
              <>
                <div style={{ marginTop: 14, padding: '8px 12px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 8, color: '#991B1B', fontSize: 13 }}>
                  {error}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
                </DialogFooter>
              </>
            )}
          </>
        )}

        {phase === 'done' && result && (
          <>
            <DialogHeader>
              <DialogTitle>'{result.slug}' restored</DialogTitle>
              <DialogDescription>
                Restored from <code>{result.restoredFrom}</code>
                {result.migrationsApplied ? `, ${result.migrationsApplied} newer migration(s) applied` : ''}. The data it
                replaced is kept at <code>{result.safetyBackupKey}</code>. The tenant is still suspended — check the
                data, then reactivate it.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>Done</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
